import { Box, Typography } from '@mui/material';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface KpiCardProps {
  label: string;
  value: string;
  delta?: string;
  deltaType?: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  accentColor?: string;
}

export default function KpiCard({
  label,
  value,
  delta,
  deltaType = 'neutral',
  icon,
  accentColor = '#1474FF',
}: KpiCardProps) {
  const deltaConfig = {
    up: { bg: 'rgba(5,150,105,0.10)', color: '#059669', border: 'rgba(5,150,105,0.22)' },
    down: { bg: 'rgba(220,38,38,0.10)', color: '#dc2626', border: 'rgba(220,38,38,0.22)' },
    neutral: { bg: `${accentColor}14`, color: '#506070', border: `${accentColor}22` },
  };
  const cfg = deltaConfig[deltaType];
  const DeltaIcon = deltaType === 'up' ? TrendingUp : deltaType === 'down' ? TrendingDown : Minus;

  return (
    <Box
      sx={{
        background: `linear-gradient(160deg, ${accentColor}05 0%, #FFFFFF 35%)`,
        border: '1px solid rgba(255,255,255,0.70)',
        boxShadow: [
          `inset 0 2.5px 0 ${accentColor}`,
          `0 4px 24px ${accentColor}18`,
          '0 1px 4px rgba(0,0,0,0.04)',
          'inset 0 -1px 0 rgba(0,0,0,0.02)',
        ].join(', '),
        borderRadius: 'var(--radius-lg)',
        p: 2.5,
        transition: 'transform 0.20s ease, box-shadow 0.20s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: [
            `inset 0 2.5px 0 ${accentColor}`,
            `0 8px 32px ${accentColor}22`,
            '0 2px 8px rgba(0,0,0,0.06)',
            'inset 0 -1px 0 rgba(0,0,0,0.02)',
          ].join(', '),
        },
      }}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-md)',
              background: `${accentColor}12`,
              border: `1px solid ${accentColor}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: accentColor,
            }}
          >
            {icon}
          </Box>
        </Box>
        {delta && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.375,
              bgcolor: cfg.bg,
              color: cfg.color,
              border: `1px solid ${cfg.border}`,
              borderRadius: '20px',
              px: 1,
              py: '3px',
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.01em',
            }}
          >
            <DeltaIcon size={9} />
            {delta}
          </Box>
        )}
      </Box>

      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 800,
          color: 'var(--color-text-primary)',
          lineHeight: 1,
          mb: 0.5,
          letterSpacing: '-0.6px',
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: 'var(--color-text-muted)', fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}
